import { FETCH_ERROR } from "@boark/shared/consts/fetch-error";
import { MaintenancePlanActionCreate } from "@boark/shared/types/maintenance-plan-action-create";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createMaintenancePlanAction,
  deleteMaintenancePlanAction,
  getMaintenancePlanActions,
} from "../services/maintenance-plan-action";

export function maintenancePlanActionsQueryKey(maintenancePlanId: string) {
  return ["maintenancePlanActions", maintenancePlanId] as const;
}

export function useGetMaintenancePlanActions(maintenancePlanId: string) {
  return useQuery({
    queryKey: maintenancePlanActionsQueryKey(maintenancePlanId),
    queryFn: () => getMaintenancePlanActions(maintenancePlanId),
    retry: (_failureCount, error: unknown) =>
      error !== FETCH_ERROR.UNAUTHORIZED,
  });
}

export function useCreateMaintenancePlanAction(maintenancePlanId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (action: MaintenancePlanActionCreate) => {
      const created = await createMaintenancePlanAction(
        maintenancePlanId,
        action,
      );
      queryClient.setQueryData(
        maintenancePlanActionsQueryKey(maintenancePlanId),
        (existing) =>
          [created, ...(existing ?? [])].sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
          ),
      );
      await queryClient.invalidateQueries({ queryKey: ["maintenancePlans"] });
      return created;
    },
  });
}

export function useDeleteMaintenancePlanAction(maintenancePlanId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (actionId: string) => {
      await deleteMaintenancePlanAction(maintenancePlanId, actionId);
      queryClient.setQueryData(
        maintenancePlanActionsQueryKey(maintenancePlanId),
        (existing) => existing?.filter((item) => item.id !== actionId) ?? [],
      );
      await queryClient.invalidateQueries({ queryKey: ["maintenancePlans"] });
    },
  });
}
